<script setup lang="ts">
/**
 * A point in time as „vor 5 Minuten“, kept current: every instance on the
 * page shares one clock that ticks while any is mounted (every 30 s while
 * something is under an hour old, else once a minute). The absolute date and
 * time is the `title`, and `<time datetime>` carries the machine value.
 *
 * Unreadable input renders nothing unless `fallback` is given.
 */
import { computed, inject, onBeforeUnmount, watchEffect } from 'vue';
import { i18nInjectionKey } from '../internal';
import { subscribeClock } from './clock';
import { formatDateTimeLong, formatRelativeTime, toDate, type NxDateLike } from './relativeTime';

const props = withDefaults(
  defineProps<{
    value: NxDateLike;
    /** `long` „vor 5 Minuten“ (default), `short`/`narrow` „vor 5 Min.“. */
    format?: Intl.RelativeTimeFormatStyle;
    /** Beyond this many days the absolute date is shown. */
    maxDays?: number;
    /** Shown when `value` cannot be read. */
    fallback?: string;
    locale?: string;
  }>(),
  { format: 'long', maxDays: 30, fallback: '', locale: undefined }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);

const clock = subscribeClock();
onBeforeUnmount(() => clock.release());

const date = computed(() => toDate(props.value));
watchEffect(() => {
  const d = date.value;
  clock.setFast(!!d && Math.abs(clock.now.value - d.getTime()) < 3_600_000);
});

const text = computed(() =>
  date.value
    ? formatRelativeTime(date.value, {
        now: clock.now.value,
        locale: locale.value,
        style: props.format,
        maxDays: props.maxDays,
      })
    : props.fallback
);
const title = computed(() => formatDateTimeLong(date.value, locale.value));
</script>

<template>
  <time
    v-if="date"
    class="nx-relative-time"
    :datetime="date.toISOString()"
    :title="title"
    data-testid="nx-relative-time"
  >
    {{ text }}
  </time>
  <span v-else-if="props.fallback" class="nx-relative-time is-empty">{{ props.fallback }}</span>
</template>

<style scoped>
.nx-relative-time {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
